import { css } from '@storyofams/react-ui';
import { withStory, WithStoryProps } from '@storyofams/storyblok-toolkit';
import { GetStaticProps, GetStaticPaths, NextPage } from 'next';
import { useRouter } from 'next/router';
import SbEditable from 'storyblok-react';

import { Seo, Container } from '~components';
import { enhancedStaticProps, getTranslationForLocale, withLayout } from '~lib';
import { Header as ProductListingHeader } from '~components/products';
import Grid from '~components/products/Grid';
import { CollectionSection } from '~components/sections';
import { getShopifySdk } from '~lib/shopify/client';
import { ProductTagsQuery } from '~lib/shopify/sdk';
import { storyblokSdk } from '~lib/storyblok/client';
import {
  CollectionComponent,
  CollectionItem,
  SubcollectionItems,
} from '~lib/storyblok/sdk';

interface ProductListingPageProps extends WithStoryProps {
  tags: ProductTagsQuery['productTags'];
  filtersMapping?: any;
  parentCollection?: CollectionItem;
  subcollections?: SubcollectionItems;
}

const ProductListingPage: NextPage<ProductListingPageProps> = ({
  parentCollection,
  story,
  subcollections,
  filtersMapping,
}) => {
  const { query } = useRouter();
  const { content } = story;

  const collection = [
    content?.collection?.item,
    ...(subcollections?.items?.length
      ? subcollections.items.map(({ content }) => content.collection?.item)
      : []),
  ];

  return (
    <>
      <Seo story={story} />

      <Container pt={[3, 6]} pb={[3, 5]}>
        <SbEditable content={content as Required<CollectionComponent>}>
          <ProductListingHeader
            removeBackButton={false}
            content={content.header[0]}
          />
        </SbEditable>
      </Container>

      {!!subcollections?.items?.length && (
        <CollectionSection
          py={[0, 0]}
          px={[2, 10]}
          css={css({
            '.swiper-slide': {
              width: ['203px', '237px'],
            },
          })}
          prefix={query?.slug?.[0]}
          items={subcollections?.items}
        />
      )}

      <Container pt={!!subcollections?.items?.length ? [3, 10] : 0}>
        <Grid
          filtersMapping={filtersMapping}
          hidesFilters={content?.hidesFilters}
          collection={collection}
        />
      </Container>
    </>
  );
};

export default withLayout(withStory(ProductListingPage));

export const getStaticProps: GetStaticProps<ProductListingPageProps> =
  enhancedStaticProps(
    async ({ defaultLocale, locale, params: { slug }, sdk }) => {
      let story = null;
      let subcollections = null;
      let notFound = false;
      let parentCollection = null;
      const { lang, needsTranslation } = getTranslationForLocale(
        locale,
        defaultLocale,
      );

      const shopifySdk = getShopifySdk(lang);

      const { productTags } = await shopifySdk.productTags();

      const filtersMapping =
        (await sdk.filtersMapping())?.FiltersItem?.content || {};

      const handle = !Array.isArray(slug) ? [slug] : slug;
      const localeUrl = needsTranslation ? `${lang}/` : '';

      try {
        if (handle?.length === 1) {
          story = (
            await sdk.collectionItem({ id: `${localeUrl}shop/${handle?.[0]}` })
          ).CollectionItem;

          const composedSlug = `${localeUrl}shop/${handle?.[0]}`;
          if (![composedSlug, `${composedSlug}/`].includes(story?.full_slug)) {
            throw new Error('Not found');
          }

          subcollections = (
            await sdk.subcollections({
              prefix: `${localeUrl}shop/${handle?.[0]}`,
            })
          ).SubcollectionItems;
        } else {
          story = (
            await sdk.subcollection({
              id: `${localeUrl}shop/${handle?.join('/')}`,
            })
          ).SubcollectionItem;

          parentCollection = (
            await sdk.collectionItem({ id: `${localeUrl}shop/${handle?.[0]}` })
          ).CollectionItem;
        }
      } catch (e) {
        notFound = true;
      }

      if (notFound || !story) {
        return {
          props: {
            story: {},
            tags: {},
            filtersMapping: {},
            subcollections,
            parentCollection,
          },
          notFound: true,
          revalidate: 60,
        };
      }

      return {
        props: {
          story,
          tags: productTags,
          filtersMapping,
          subcollections,
          parentCollection,
        },
        notFound: notFound || !story,
        revalidate: 60,
      };
    },
  );

export const getStaticPaths: GetStaticPaths = async ({ locales }) => {
  const collections = (await storyblokSdk.collectionItems()).CollectionItems;
  const subcollections = (await storyblokSdk.subcollections({ prefix: '' }))
    .SubcollectionItems;

  return {
    paths: [
      ...collections?.items?.map(({ slug }) => ({
        slug: [slug],
      })),
      ...subcollections?.items?.map(({ full_slug }) => ({
        slug: full_slug?.replace('shop/', '')?.split('/'),
      })),
    ]
      ?.map((params) => locales.map((locale) => ({ params, locale })))
      ?.flat(),
    fallback: 'blocking',
  };
};
